/**
 * When to interrupt a student because their footage is not reaching the
 * server.
 *
 * The corner indicator already says "upload is behind", and a student deep in
 * a question does not read the corner. A backlog this deep means the uplink is
 * losing to the encoder, and the one thing the student can actually do about
 * it — move the phone somewhere with signal, get off the congested network —
 * has to be asked for out loud.
 *
 * The rule is a LADDER, not a threshold: nagging every time the count ticks up
 * would put a dialog over the paper every few seconds, which costs exam time
 * and teaches the student to dismiss it without reading. One at ten, the next
 * not before twenty, then thirty.
 */

/** Both the first threshold and the gap between the ones after it. */
export const BACKLOG_STEP = 10;

/**
 * Whether to show the nudge now.
 *
 * `stuck` must be evidence that the footage is NOT LANDING — an upload health
 * of "stalled", not merely "behind". A deep queue on its own is not a reason
 * to interrupt anyone: the encoder can outrun a slow-but-working uplink for a
 * minute at a time while every segment still arrives, and `useAdaptiveBitrate`
 * is already dropping the bitrate to fix exactly that. Interrupting there
 * would put a box over the paper of a student whose recording is fine.
 *
 * `shownAt` is the backlog depth the last nudge was shown at, or 0 if none has
 * been — and it must be reset to 0 when the queue drains, or a bad patch early
 * in the exam raises the bar for the rest of it.
 */
export function shouldNudge(
  pending: number,
  shownAt: number,
  stuck: boolean,
): boolean {
  if (!stuck) return false;
  if (pending <= BACKLOG_STEP) return false;
  return pending >= shownAt + BACKLOG_STEP;
}

/**
 * What the student is told, which depends on whether this sitting uses the
 * phone.
 *
 * Naming a device the exam never asked for is the failure this app has made
 * before: an instruction about "your phone" to someone sitting with only a
 * laptop reads as a fault they cannot fix.
 */
export function nudgeMessage(withPhone: boolean): string {
  return withPhone
    ? "Your recordings are not reaching the server. Your internet looks slow — check that your phone is still connected to the internet and has a good signal, and move closer to your router if you can."
    : "Your recordings are not reaching the server. Your internet looks slow — check your connection and move closer to your router if you can.";
}
